import React, { useState, useEffect } from 'react';
import Confetti from 'react-confetti';

// House colors
const HOUSE_COLORS = {
  Royals: { bg: 'bg-blue-100', border: 'border-blue-500', text: 'text-blue-800', glow: 'shadow-[0_0_15px_rgba(59,130,246,0.6)]' },
  Titans: { bg: 'bg-purple-100', border: 'border-purple-500', text: 'text-purple-800', glow: 'shadow-[0_0_15px_rgba(139,92,246,0.6)]' },
  Defenders: { bg: 'bg-green-100', border: 'border-green-500', text: 'text-green-800', glow: 'shadow-[0_0_15px_rgba(34,197,94,0.6)]' },
  Bouncers: { bg: 'bg-yellow-100', border: 'border-yellow-500', text: 'text-yellow-800', glow: 'shadow-[0_0_15px_rgba(234,179,8,0.6)]' }
};

// Categories
const CATEGORIES = [
  { key: 'all', name: 'Overall' },
  { key: 'LP Mini', name: 'LP Mini' },
  { key: 'LP Kiddies', name: 'LP Kiddies' },
  { key: 'UP Kiddies', name: 'UP Kiddies' },
  { key: 'HS Boys', name: 'HS Boys' },
  { key: 'HS Girls', name: 'HS Girls' }
];

export default function Home() {
  const [activeTab, setActiveTab] = useState('all');
  const [scores, setScores] = useState({});
  const [leader, setLeader] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const [prevLeader, setPrevLeader] = useState('');

  // Fetch scores for active category
  useEffect(() => {
    const fetchScores = async () => {
      try {
        const url = activeTab === 'all' 
          ? '/api/scores' 
          : `/api/scores?category=${encodeURIComponent(activeTab)}`;
        const res = await fetch(url);
        const data = await res.json();
        if (data.leader !== prevLeader && prevLeader) {
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 3000);
        }
        setScores(data.scores || {});
        setLeader(data.leader || '');
        setPrevLeader(data.leader || '');
      } catch (err) {
        console.error('Failed to load scores:', err);
      }
    };

    fetchScores();
    const interval = setInterval(fetchScores, 5000);
    return () => clearInterval(interval);
  }, [activeTab, prevLeader]);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Confetti */}
      {showConfetti && <Confetti recycle={false} numberOfPieces={300} />}

      {/* School Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">AL MAQAR ENGLISH SCHOOL | BADARIYA NAGAR</h1>
        <h2 className="text-3xl md:text-4xl font-extrabold text-red-600 mt-2">
          🏆 Athleon Sports Meet 2025–2026 🏆
        </h2>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {CATEGORIES.map(cat => (
          <button
            key={cat.key}
            onClick={() => setActiveTab(cat.key)}
            className={`px-4 py-2 rounded-full font-semibold transition-all ${
              activeTab === cat.key
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* Leading House Banner */}
      {leader && (
        <div className="text-center mb-8 animate-pulse">
          <div className="inline-block bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-6 py-3 rounded-full font-bold text-xl shadow-lg">
            🥇 Leading House: <span className="font-extrabold">{leader}</span>
          </div>
        </div>
      )}

      {/* House Score Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
        {Object.entries(scores).map(([house, score]) => {
          const color = HOUSE_COLORS[house] || {};
          const isLeader = house === leader;
          return (
            <motion.div
              key={house}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ 
                scale: isLeader ? 1.05 : 1,
                opacity: 1,
                y: isLeader ? -10 : 0
              }}
              transition={{ type: 'spring', stiffness: 300 }}
              className={`rounded-xl p-6 border-2 ${color.bg} ${color.border} ${isLeader ? color.glow : ''} transition-all duration-300`}
              style={{ transform: isLeader ? 'scale(1.02)' : 'scale(1)' }}
            >
              <div className="text-center">
                <h3 className={`text-2xl font-bold ${color.text} mb-2`}>{house}</h3>
                <div className={`text-5xl font-extrabold ${isLeader ? 'text-red-600' : 'text-gray-800'} transition-colors`}>
                  {score}
                </div>
                {isLeader && (
                  <div className="mt-3 text-yellow-600 font-bold flex items-center justify-center">
                    🥇 Leading!
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Footer */}
      <footer className="mt-12 text-center text-gray-600 text-sm">
        Designed and Developed by{' '}
        <a
          href="https://mcmedia.in"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 font-semibold hover:underline"
        >
          MCMedia
        </a>
      </footer>
    </div>
  );
}

// Simple motion wrapper if you don't want to install framer-motion
const motion = {
  div: ({ children, ...props }) => <div {...props}>{children}</div>
};